import { useState } from 'react';
import { FieldPath, SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { maskPhone, unmaskNumber } from '@givebutter/util';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { transformAddressToRequest } from 'api/account';
import { createAuctionBidder } from 'api/campaigns';
import * as ContactForm from 'assets/js/pages/contacts/ContactForm';
import { AddressForm } from 'components/address-form/AddressForm';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useGivebutterForm } from 'components/form';
import { ManualBidderFormValues } from 'components/pages/campaign-settings/campaign-auction/auction-bidders/types';
import { useCurrentAuction } from 'customHooks/useCurrentAuction';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { checkAddressErrors, useFormServerErrors } from 'customHooks/useFormServerErrors';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { TransactionDonation } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const AddManualBidderDrawer = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { goBack } = useDialogRoute();
    const [saveAndExit, setSaveAndExit] = useState(false);
    const { data: campaign } = useCurrentCampaign();
    const { data: auction } = useCurrentAuction();
    const campaignId = campaign.id;
    const auctionId = auction.id;

    const auctionRequiresPaymentMethod = auction.require_payment_method;
    const completeRegistrationText = `A notification will be sent to the email on file requesting
        the bidder to complete their registration${
            auctionRequiresPaymentMethod ? ' and add a payment method.' : '.'
        }`;

    const methods = useGivebutterForm<ManualBidderFormValues>({
        defaultValues: {
            line1: '',
            line2: '',
            city: '',
            zip: '',
            stateUs: '',
            stateIntl: '',
            country: '',
            isPrimary: true,
            enableManualBidderNumber: false,
            manualBidderNumber: null,
            firstName: '',
            lastName: '',
            middleName: '',
            emails: [{ value: '', is_primary: true, type: 'personal' }],
            phones: [{ value: '', is_primary: true, type: 'home' }],
            selectedContact: [],
        },
    });

    const { setError, path, control, handleSubmit, watch, reset, resetFormButton } = methods;
    const { setAddressErrors } = useFormServerErrors(methods);

    const [rawSelectedContact, enableManualBidderNumber] = watch([
        'selectedContact',
        'enableManualBidderNumber',
    ]);

    const selectedContact = rawSelectedContact?.[0];
    const isNewContact = selectedContact?.id === ContactForm.NewContactId;

    const { mutate: submit, isPending: isSubmitting } = useMutation({
        mutationFn: (formValues: ManualBidderFormValues) => {
            const state = formValues.country === 'USA' ? formValues.stateUs : formValues.stateIntl;

            const number = formValues.manualBidderNumber
                ? unmaskNumber(formValues.manualBidderNumber)
                : undefined;

            return createAuctionBidder(campaignId, auctionId, {
                first_name: formValues.firstName,
                middle_name: formValues.middleName,
                last_name: formValues.lastName,
                email: formValues.emails[0]?.value,
                phone: formValues.phones[0]?.value,
                shipping_address: transformAddressToRequest({ ...formValues, state }),
                number,
                email_subscription: formValues.allowEngageEmail,
                phone_subscription: formValues.allowEngageSms,
                address_subscription: formValues.allowEngageAddress,
                anonymous: formValues.makeBidderAnonymous,
                request_bidder_payment_method: formValues.requestBidderPaymentMethod,
            });
        },
        onSuccess: () => {
            toast.success('Bidder successfully added!');

            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.auctionBidders(campaignId, auctionId),
            });

            if (saveAndExit) {
                goBack();
            } else {
                reset();
            }
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data?.message ?? `An unknown error occurred.`);
            const errors = error.response?.data.errors;
            if (errors) {
                const hasAddressErrors = checkAddressErrors(errors);
                if (hasAddressErrors) {
                    setAddressErrors(errors);
                }

                Object.keys(errors).forEach(key => {
                    const normalizedKey = transformServerKeyIntoFormKey(
                        key as Parameters<typeof transformServerKeyIntoFormKey>[0],
                    );

                    if (normalizedKey) {
                        setError(normalizedKey as FieldPath<ManualBidderFormValues>, {
                            message: errors[key][0],
                        });
                    }
                });
            }

            resetFormButton();
        },
    });

    const isExistingContact = !isNewContact && !!selectedContact;

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<ManualBidderFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>Add bidder</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className="flex flex-col gap-8">
                        <Form.Container>
                            <Form.Label name={path('selectedContact')}>Bidder</Form.Label>
                            {(!selectedContact || isNewContact) && <ContactForm.SelectContact />}
                            {!!selectedContact && !isNewContact && (
                                <div className={'flex items-start gap-2'}>
                                    <div className={'flex flex-col gap-1'}>
                                        <p className={'font-medium'}>
                                            {selectedContact.value?.first_name}{' '}
                                            {selectedContact.value?.last_name}
                                        </p>
                                        <p className={'text-gray-600'}>
                                            {selectedContact.value?.primary_email}
                                        </p>
                                        <p className={'text-gray-600'}>
                                            {maskPhone(selectedContact.value?.primary_phone ?? '')}
                                        </p>
                                    </div>
                                    <Button
                                        variant="solid"
                                        onClick={() => reset()}
                                        color="neutral"
                                        size="xs"
                                        shape="square"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                </div>
                            )}
                            <Form.Error name={path('selectedContact')} />
                        </Form.Container>

                        {selectedContact && (
                            <Form.Container className={'flex-col gap-6'}>
                                <div className={'flex w-full flex-col gap-1'}>
                                    <div className={'flex items-center justify-between'}>
                                        <Form.Label
                                            name={path('requestBidderPaymentMethod')}
                                            className={'leading-none'}
                                        >
                                            {auctionRequiresPaymentMethod
                                                ? 'Request bidder to add payment method'
                                                : 'Request bidder to complete registration'}
                                        </Form.Label>
                                        <Form.Switch
                                            control={control}
                                            name={'requestBidderPaymentMethod'}
                                        />
                                    </div>
                                    <p className={'text-sm text-gray-600'}>
                                        {completeRegistrationText}
                                    </p>
                                </div>
                                <div className={'flex w-full flex-col gap-1'}>
                                    <div className={'flex items-center justify-between'}>
                                        <Form.Label
                                            name={path('enableManualBidderNumber')}
                                            className={'leading-none'}
                                        >
                                            Manually assign bidder ID #
                                        </Form.Label>
                                        <Form.Switch
                                            control={control}
                                            name={path('enableManualBidderNumber')}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-4">
                                        <p className={'text-sm text-gray-600'}>
                                            Enable if a specific number is desired and input the
                                            preferred ID manually.
                                        </p>
                                        {!!selectedContact && enableManualBidderNumber && (
                                            <div className={'flex-row gap-2'}>
                                                <Form.Container>
                                                    <Form.Label
                                                        className="sr-only"
                                                        name={path('manualBidderNumber')}
                                                    >
                                                        Bidder ID#
                                                    </Form.Label>
                                                    <Form.TextInputRoot
                                                        control={control}
                                                        name={path('manualBidderNumber')}
                                                        rules={{
                                                            required:
                                                                'Starting bidder number is required.',
                                                            validate: {
                                                                greaterThanZero: value =>
                                                                    unmaskNumber(value) >= 1 ||
                                                                    'The bidder number start must be at least 1.',
                                                            },
                                                        }}
                                                        inputRootProps={{
                                                            mask: 'whole-number',
                                                        }}
                                                    >
                                                        <Form.UseFormTextInputForm
                                                            placeholder={
                                                                'Enter the desired bidder ID #'
                                                            }
                                                        />
                                                    </Form.TextInputRoot>
                                                </Form.Container>
                                                <Form.UseFormError
                                                    name={path('manualBidderNumber')}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className={'flex w-full flex-col gap-1'}>
                                    <div className={'flex items-center justify-between'}>
                                        <Form.Label
                                            name={path('makeBidderAnonymous')}
                                            className={'leading-none'}
                                        >
                                            Make anonymous
                                        </Form.Label>
                                        <Form.Switch
                                            control={control}
                                            name={'makeBidderAnonymous'}
                                        />
                                    </div>
                                    <p className={'text-sm text-gray-600'}>
                                        The contact's name will be hidden on the public-facing
                                        campaign page.
                                    </p>
                                </div>
                            </Form.Container>
                        )}
                        {isNewContact && (
                            <ContactForm.Container>
                                <ContactForm.FirstName hideAffix />
                                <ContactForm.MiddleName />
                                <ContactForm.LastName hideAffix />
                                <ContactForm.Emails isRequired />
                                <ContactForm.Phones />
                                <AddressForm isRequired={auctionRequiresPaymentMethod} />
                                <ContactForm.Engage />
                            </ContactForm.Container>
                        )}
                        {isExistingContact && !selectedContact?.value?.primary_address && (
                            <AddressForm isRequired={auctionRequiresPaymentMethod} />
                        )}
                        {isExistingContact && !selectedContact?.value?.primary_email && (
                            <>
                                <ContactForm.Emails isRequired />
                                <ContactForm.Engage />
                            </>
                        )}
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton
                                variant={'outline'}
                                color={'neutral'}
                                noAnimation={true}
                                onClick={() => setSaveAndExit(false)}
                                isSubmitting={isSubmitting}
                            >
                                Save and add another
                            </Form.SubmitButton>
                            <Form.SubmitButton
                                isSubmitting={isSubmitting}
                                onClick={() => setSaveAndExit(true)}
                            >
                                Save
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

interface BidderValues extends TransactionDonation {
    number: number | null;
    middle_name: string;
    email_subscription: boolean;
    phone_subscription: boolean;
    address_subscription: boolean;
    anonymous: boolean;
    request_bidder_payment_method: boolean;
}

const transformServerKeyIntoFormKey = (
    serverKey: keyof BidderValues | 'campaign',
): keyof ManualBidderFormValues | null => {
    switch (serverKey) {
        case 'first_name':
            return 'firstName';
        case 'last_name':
            return 'lastName';
        case 'middle_name':
            return 'middleName';
        case 'email_subscription':
            return 'allowEngageEmail';
        case 'phone_subscription':
            return 'allowEngageSms';
        case 'address_subscription':
            return 'allowEngageAddress';
        case 'campaign':
        case 'contact_id':
            return 'selectedContact';
        case 'address_1':
        case 'address_2':
        case 'state':
        case 'zipcode':
        case 'city':
        case 'country':
            return null;
        case 'email':
            return 'emails';
        case 'phone':
            return 'phones';
        case 'number':
            return 'manualBidderNumber';
        case 'anonymous':
            return 'makeBidderAnonymous';
        case 'request_bidder_payment_method':
            return 'requestBidderPaymentMethod';
        default:
            return null;
    }
};

export { AddManualBidderDrawer };
